/* ==========================================================================
   Sauna build calculator — shared engine
   One dimension store, shared by every calculator section on the page.
   This file is loaded by several sections, so everything is guarded against
   running twice.

   Static wording is passed in from each section's settings via
   data-sauna-config, so it can be edited in the theme editor. Only values the
   calculator works out for itself are written here.
   ========================================================================== */

(function () {
  'use strict';

  if (window.SaunaCalc) return;

  var STORAGE_KEY = 'sauna-calc:dimensions';
  var EVENT_NAME = 'sauna-calc:update';

  /* ---------------------------------------------------------------- utils */

  // Accepts 0.75, "3/4", "5 1/8", '49"' etc. Returns a number.
  function parseMeasure(value) {
    if (typeof value === 'number') return isFinite(value) ? value : 0;
    if (value === null || value === undefined) return 0;

    var str = String(value).trim().replace(/["'\u2019\u201d]/g, '');
    if (!str) return 0;

    var total = 0;
    var parts = str.split(/[\s\-]+/);

    for (var i = 0; i < parts.length; i++) {
      var part = parts[i];
      if (!part) continue;

      if (part.indexOf('/') > -1) {
        var frac = part.split('/');
        var numerator = parseFloat(frac[0]);
        var denominator = parseFloat(frac[1]);
        if (!isNaN(numerator) && denominator) total += numerator / denominator;
      } else {
        var plain = parseFloat(part);
        if (!isNaN(plain)) total += plain;
      }
    }

    return total;
  }

  function num(value, fallback) {
    var parsed = parseMeasure(value);
    return parsed > 0 ? parsed : fallback || 0;
  }

  function fmt(value, decimals) {
    if (decimals === undefined || decimals === null) decimals = 0;
    var factor = Math.pow(10, decimals);
    var rounded = Math.round(value * factor) / factor;
    return rounded.toLocaleString('en-US', { maximumFractionDigits: decimals });
  }

  function roundUpTo(value, step) {
    return Math.ceil(value / step) * step;
  }

  function esc(value) {
    return String(value).replace(/[&<>"']/g, function (char) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
  }

  // 93.125 -> 93 1/8"    82.5625 -> 82 9/16"
  function inchLabel(value, denominator) {
    denominator = denominator || 32;

    var whole = Math.floor(value);
    var numerator = Math.round((value - whole) * denominator);

    if (numerator === denominator) {
      whole += 1;
      numerator = 0;
    }

    if (numerator === 0) return whole + '"';

    var den = denominator;
    while (numerator % 2 === 0 && den % 2 === 0) {
      numerator /= 2;
      den /= 2;
    }

    return (whole ? whole + ' ' : '') + numerator + '/' + den + '"';
  }

  function feetInchLabel(totalInches) {
    var feet = Math.floor(totalInches / 12);
    var inches = totalInches - feet * 12;
    if (Math.round(inches * 32) === 0) return feet + "'";
    return feet + "' " + inchLabel(inches);
  }

  // Wording set in the theme editor. If the key is present the setting wins,
  // even when it's been cleared — emptying a field in the editor should empty
  // it on the page. The fallback only covers a key that isn't sent at all.
  function label(config, key, fallback) {
    var labels = (config && config.labels) || {};
    if (Object.prototype.hasOwnProperty.call(labels, key)) return String(labels[key]);
    return fallback || '';
  }

  /* --------------------------------------------------------- board maths */

  /**
   * How many boards of `boardLenFt` are needed to produce `runCount` runs,
   * each `runLenFt` long, including a waste buffer.
   *
   * If the board is at least as long as the run, we work out how many runs can
   * be cut from one board (offcuts shorter than a run are waste).
   * If the board is shorter than the run, the runs are butt-jointed, so we work
   * in total linear feet instead.
   */
  function boardsForRuns(runLenFt, runCount, boardLenFt, buffer) {
    if (runCount <= 0 || runLenFt <= 0 || boardLenFt <= 0) return 0;

    if (boardLenFt >= runLenFt) {
      var perBoard = Math.floor(boardLenFt / runLenFt);
      var needed = Math.ceil(runCount * (1 + buffer));
      return Math.ceil(needed / perBoard);
    }

    return Math.ceil((runLenFt * runCount * (1 + buffer)) / boardLenFt);
  }

  /**
   * Drop board lengths that earn nothing. A longer length is removed when a
   * shorter one already needs the same number of pieces, or fewer, everywhere
   * it's used — the longer board there is more money and more offcut for an
   * identical result, so there's no reason to show it.
   */
  function usefulLengths(lengths, countsFor) {
    var sorted = lengths.slice().sort(function (a, b) {
      return a - b;
    });

    var kept = [];

    sorted.forEach(function (len) {
      var counts = countsFor(len);

      var beaten = kept.some(function (keptLen) {
        return countsFor(keptLen).every(function (value, index) {
          return value <= counts[index];
        });
      });

      if (!beaten) kept.push(len);
    });

    return kept.length ? kept : sorted.slice(0, 1);
  }

  /**
   * Which direction to run boards across a rectangle (a ceiling or a floor).
   *
   * A direction that can be spanned by a stocked length wins outright, because
   * joining every run mid-span to save a little material is a poor trade in a
   * finished surface.
   *
   * When both directions can be spanned, it compares the material actually
   * bought — pieces times their length — rather than the piece count, because a
   * count is meaningless across different board lengths: 22 ten-foot boards is
   * 220 linear feet, more than 25 eight-foot boards at 200.
   */
  function runDirection(d, lengths, widths, buffer) {
    function canSpan(runLenFt) {
      return lengths.some(function (len) {
        return len >= runLenFt;
      });
    }

    function cost(alongLength) {
      var runLenFt = alongLength ? d.L : d.W;
      var spanning = lengths.filter(function (len) {
        return len >= runLenFt;
      });
      var pool = spanning.length ? spanning : lengths;
      var total = 0;

      widths.forEach(function (width) {
        var coverage = num(width.coverage, width.nominal);
        var runs = alongLength ? Math.ceil(d.widthIn / coverage) : Math.ceil(d.lengthIn / coverage);
        var best = Infinity;

        pool.forEach(function (len) {
          best = Math.min(best, boardsForRuns(runLenFt, runs, len, buffer) * len);
        });

        total += best;
      });

      return total;
    }

    var spansAlongLength = canSpan(d.L);
    var spansAlongWidth = canSpan(d.W);

    if (spansAlongLength !== spansAlongWidth) return spansAlongLength;

    var alongLengthCost = cost(true);
    var alongWidthCost = cost(false);

    // On a tie, run the shorter boards.
    if (alongLengthCost === alongWidthCost) return d.L <= d.W;
    return alongLengthCost < alongWidthCost;
  }

  /* ------------------------------------------------------- dimension store */

  var raw = null;
  var dims = null;

  // Strapping and panelling thickness. Set by the dimensions section so every
  // calculator works from the same wall build-up.
  var buildup = { strapping: 0.75, panelling: 0.75 };

  function computeDims(input) {
    if (!input) return null;

    var lengthIn = (input.lengthFt || 0) * 12 + (input.lengthIn || 0);
    var widthIn = (input.widthFt || 0) * 12 + (input.widthIn || 0);
    var heightIn = (input.heightFt || 0) * 12 + (input.heightIn || 0);

    if (lengthIn <= 0 || widthIn <= 0 || heightIn <= 0) return null;

    var L = lengthIn / 12;
    var W = widthIn / 12;
    var H = heightIn / 12;

    // Finished inside dimensions, once strapping and panelling are on.
    // Length and width lose the build-up twice over; height loses it once,
    // at the ceiling.
    var perSide = buildup.strapping + buildup.panelling;
    var finishedLengthIn = Math.max(0, lengthIn - 2 * perSide);
    var finishedWidthIn = Math.max(0, widthIn - 2 * perSide);
    var finishedHeightIn = Math.max(0, heightIn - perSide);

    return {
      lengthIn: lengthIn,
      widthIn: widthIn,
      heightIn: heightIn,
      L: L,
      W: W,
      H: H,
      perimeterFt: 2 * (L + W),
      floorArea: L * W,
      ceilingArea: L * W,
      wallArea: 2 * (L + W) * H,
      volume: L * W * H,
      strappingIn: buildup.strapping,
      panellingIn: buildup.panelling,
      buildupPerSide: perSide,
      finishedLengthIn: finishedLengthIn,
      finishedWidthIn: finishedWidthIn,
      finishedHeightIn: finishedHeightIn,
      finishedVolume: (finishedLengthIn / 12) * (finishedWidthIn / 12) * (finishedHeightIn / 12)
    };
  }

  function setBuildup(next) {
    // Zero is a real answer here — someone may panel straight onto the studs —
    // so only fall back to the default when the field is left empty.
    function thickness(value, fallback) {
      if (value === null || value === undefined || String(value).trim() === '') return fallback;
      var parsed = parseMeasure(value);
      return isFinite(parsed) && parsed >= 0 ? parsed : fallback;
    }

    buildup = {
      strapping: thickness(next && next.strapping, 0.75),
      panelling: thickness(next && next.panelling, 0.75)
    };
    dims = computeDims(raw);
  }

  function loadStored() {
    try {
      var stored = window.localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      var parsed = JSON.parse(stored);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  function save() {
    try {
      if (raw) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(raw));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      /* storage blocked — values still work for this page view */
    }
  }

  function broadcast() {
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { raw: raw, dims: dims } }));
  }

  raw = loadStored();
  dims = computeDims(raw);

  /* -------------------------------------------------------------- markup */

  function stat(name, value, modifier) {
    return (
      '<div class="sauna-stat">' +
      '<span class="sauna-stat__label">' +
      esc(name) +
      '</span>' +
      '<span class="sauna-stat__value' +
      (modifier ? ' ' + modifier : '') +
      '">' +
      esc(value) +
      '</span>' +
      '</div>'
    );
  }

  function statGrid(stats) {
    var modifier = stats.length >= 4 ? ' sauna-stats--4' : stats.length === 3 ? ' sauna-stats--3' : '';
    return '<div class="sauna-stats' + modifier + '">' + stats.join('') + '</div>';
  }

  function group(title, body) {
    return (
      '<div class="sauna-group">' +
      (title ? '<h3 class="sauna-group__title">' + esc(title) + '</h3>' : '') +
      body +
      '</div>'
    );
  }

  function table(headers, rows) {
    var head = headers
      .map(function (header) {
        return '<th scope="col">' + esc(header) + '</th>';
      })
      .join('');

    var body = rows
      .map(function (row) {
        var cells = row.cells || row;
        var className = row.className ? ' class="' + row.className + '"' : '';

        var markup = cells
          .map(function (cell, index) {
            if (index === 0) return '<th scope="row">' + cell + '</th>';
            return '<td>' + cell + '</td>';
          })
          .join('');

        return '<tr' + className + '>' + markup + '</tr>';
      })
      .join('');

    return (
      '<div class="sauna-tablewrap"><table class="sauna-table"><thead><tr>' +
      head +
      '</tr></thead><tbody>' +
      body +
      '</tbody></table></div>'
    );
  }

  function qty(count, unit) {
    if (!count) return '<span class="sauna-table__unit">&mdash;</span>';
    return (
      '<span class="sauna-table__qty">' +
      fmt(count, 0) +
      '</span>' +
      (unit ? ' <span class="sauna-table__unit">' + esc(unit) + '</span>' : '')
    );
  }

  function inches(value) {
    if (!value) return '<span class="sauna-table__unit">&mdash;</span>';
    return '<span class="sauna-table__qty">' + fmt(value, 0) + '&quot;</span>';
  }

  function lengthHeaders(lengths, first) {
    return [first].concat(
      lengths.map(function (len) {
        return len + "' boards";
      })
    );
  }

  /* ----------------------------------------------------------- renderers */

  var renderers = {};

  renderers.heater = function (d, config) {
    var perKw = num(config.cuftPerKw, 50);
    var volume = d.finishedVolume;
    var minKw = roundUpTo(volume / perKw, 0.5);
    return {
      html: statGrid([
        stat('Framed volume', fmt(d.volume, 0) + ' cu ft'),
        stat('Finished volume', fmt(volume, 0) + ' cu ft'),
        stat('Minimum power', fmt(minKw, 1) + ' kW'),
        stat('Wood stove rating', fmt(volume, 0) + ' cu ft')
      ])
    };
  };

  renderers.insulation = function (d, config) {
    var coverage24 = num(config.coverage24, 60);
    var coverage26 = num(config.coverage26, 40);
    var area = d.wallArea + d.ceilingArea;

    var bags24 = Math.ceil(area / coverage24);
    var bags26 = Math.ceil(area / coverage26);

    var rows = [
      ['2x4 studs', fmt(coverage24, 0) + ' sq ft', qty(bags24, bags24 === 1 ? 'bag' : 'bags')],
      ['2x6 studs', fmt(coverage26, 0) + ' sq ft', qty(bags26, bags26 === 1 ? 'bag' : 'bags')]
    ];

    return {
      html:
        statGrid([
          stat('Wall area', fmt(d.wallArea, 0) + ' sq ft'),
          stat('Ceiling area', fmt(d.ceilingArea, 0) + ' sq ft'),
          stat('Total to insulate', fmt(area, 0) + ' sq ft')
        ]) + group('', table(['Stud size', 'Coverage per bag', 'Bags needed'], rows))
    };
  };

  renderers.vapour = function (d, config) {
    var rollWidthIn = num(config.rollWidthIn, 49);
    var rollLengthFt = num(config.rollLengthFt, 78);
    var tapePerRoll = num(config.tapePerRoll, 1);
    var overlap = (config.overlap || 0) / 100;

    var coverage = (rollWidthIn / 12) * rollLengthFt;
    var area = d.wallArea + d.ceilingArea;

    // Seams are lapped rather than butted, so more material is needed than
    // the bare surface area.
    var rolls = Math.ceil((area * (1 + overlap)) / coverage);
    var tape = Math.ceil(rolls * tapePerRoll);

    return {
      html: statGrid([
        stat('Area to cover', fmt(area, 0) + ' sq ft'),
        stat('Vapour barrier', fmt(rolls, 0) + (rolls === 1 ? ' roll' : ' rolls')),
        stat('Foil tape', fmt(tape, 0) + (tape === 1 ? ' roll' : ' rolls'))
      ])
    };
  };

  renderers.strapping = function (d, config) {
    var lengths = config.lengths || [];
    var wallSpacing = num(config.wallSpacing, 16);
    var floorSpacing = num(config.floorSpacing, 16);
    var buffer = (config.buffer || 0) / 100;

    if (!lengths.length) {
      return { html: '<p class="sauna-empty">No strapping lengths are turned on for this section yet.</p>' };
    }

    // Walls: horizontal rows up each wall, continuing around the perimeter.
    var wallRows = Math.floor(d.heightIn / wallSpacing) + 1;
    var wallLf = wallRows * d.perimeterFt;

    // Ceiling: runs across the width, each one the length of the room.
    var ceilingRuns = Math.floor(d.widthIn / wallSpacing) + 1;
    var ceilingLf = ceilingRuns * d.L;

    var wallCeilingLf = wallLf + ceilingLf;

    // Floor sleepers: runs across the width, each one the length of the room.
    var floorRuns = Math.floor(d.widthIn / floorSpacing) + 1;
    var floorLf = floorRuns * d.L;

    var totalLf = wallCeilingLf + floorLf;

    function countsFor(len) {
      return [
        Math.ceil((wallCeilingLf * (1 + buffer)) / len),
        Math.ceil((floorLf * (1 + buffer)) / len),
        Math.ceil((totalLf * (1 + buffer)) / len)
      ];
    }

    var shown = usefulLengths(lengths, countsFor);

    var rows = shown.map(function (len) {
      var counts = countsFor(len);
      return [len + "' lengths", qty(counts[0]), qty(counts[1]), qty(counts[2])];
    });

    return {
      html:
        statGrid([
          stat('Walls & ceiling', fmt(wallCeilingLf, 0) + ' linear ft'),
          stat('Floor', fmt(floorLf, 0) + ' linear ft'),
          stat('Total', fmt(totalLf, 0) + ' linear ft')
        ]) + group('', table(['Strap length', 'Walls & ceiling', 'Floor', 'Total'], rows))
    };
  };

  renderers.panelling = function (d, config) {
    var lengths = config.lengths || [];
    var widths = config.widths || [];
    var buffer = (config.buffer || 0) / 100;
    var horizontal = config.orientation === 'horizontal';
    var doorBoards = Math.max(0, Math.round(num(config.doorBoards, 0)));

    if (!lengths.length || !widths.length) {
      return { html: '<p class="sauna-empty">No panel lengths or widths are turned on for this section yet.</p>' };
    }

    function wallBoards(coverage, len) {
      if (horizontal) {
        var rows = Math.ceil(d.heightIn / coverage);
        return boardsForRuns(d.L, rows * 2, len, buffer) + boardsForRuns(d.W, rows * 2, len, buffer);
      }
      var runs = 2 * Math.ceil(d.lengthIn / coverage) + 2 * Math.ceil(d.widthIn / coverage);
      return boardsForRuns(d.H, runs, len, buffer);
    }

    function ceilingBoards(coverage, len, alongLength) {
      if (alongLength) return boardsForRuns(d.L, Math.ceil(d.widthIn / coverage), len, buffer);
      return boardsForRuns(d.W, Math.ceil(d.lengthIn / coverage), len, buffer);
    }

    var alongLength = runDirection(d, lengths, widths, buffer);
    var ceilingRunFt = alongLength ? d.L : d.W;
    var ceilingRunLabel = alongLength ? feetInchLabel(d.lengthIn) : feetInchLabel(d.widthIn);
    var wallRunFt = horizontal ? Math.max(d.L, d.W) : d.H;

    function countsFor(len) {
      var counts = [];
      widths.forEach(function (width) {
        var coverage = num(width.coverage, width.nominal);
        counts.push(wallBoards(coverage, len));
        counts.push(ceilingBoards(coverage, len, alongLength));
      });
      return counts;
    }

    var shown = usefulLengths(lengths, countsFor);
    var headers = lengthHeaders(shown, 'Where');
    var needsJoining = false;

    var html = statGrid([
      stat('Wall area', fmt(d.wallArea, 0) + ' sq ft'),
      stat('Ceiling area', fmt(d.ceilingArea, 0) + ' sq ft'),
      stat('Wall boards', horizontal ? 'Horizontal' : 'Vertical'),
      stat('Ceiling boards', 'Along ' + ceilingRunLabel + ' wall')
    ]);

    widths.forEach(function (width) {
      var coverage = num(width.coverage, width.nominal);

      var wallRow = [label(config, 'walls', 'Walls')];
      var ceilingRow = [label(config, 'ceiling', 'Ceiling')];
      var doorRow = [label(config, 'door', 'Door trim & jambs')];
      var totalRow = [label(config, 'total', 'Total')];

      shown.forEach(function (len) {
        var wall = wallBoards(coverage, len);
        var ceiling = ceilingBoards(coverage, len, alongLength);

        if (len < wallRunFt || len < ceilingRunFt) needsJoining = true;

        wallRow.push(qty(wall, len < wallRunFt ? '*' : ''));
        ceilingRow.push(qty(ceiling, len < ceilingRunFt ? '*' : ''));
        doorRow.push(qty(doorBoards));
        totalRow.push(qty(wall + ceiling + doorBoards));
      });

      var rows = [wallRow, ceilingRow];
      if (doorBoards > 0) rows.push(doorRow);
      rows.push({ cells: totalRow, className: 'sauna-table__row--total' });

      html += group(width.nominal + '" Boards', table(headers, rows));
    });

    return {
      html: html,
      notes: needsJoining ? [label(config, 'joining', '')] : []
    };
  };

  renderers.flooring = function (d, config) {
    var lengths = config.lengths || [];
    var widths = config.widths || [];
    var buffer = (config.buffer || 0) / 100;

    if (!lengths.length || !widths.length) {
      return { html: '<p class="sauna-empty">No board lengths or widths are turned on for this section yet.</p>' };
    }

    function floorBoards(coverage, len, alongLength) {
      if (alongLength) return boardsForRuns(d.L, Math.ceil(d.widthIn / coverage), len, buffer);
      return boardsForRuns(d.W, Math.ceil(d.lengthIn / coverage), len, buffer);
    }

    var alongLength = runDirection(d, lengths, widths, buffer);
    var runFt = alongLength ? d.L : d.W;
    var runLabel = alongLength ? feetInchLabel(d.lengthIn) : feetInchLabel(d.widthIn);

    function countsFor(len) {
      return widths.map(function (width) {
        return floorBoards(num(width.coverage, width.nominal), len, alongLength);
      });
    }

    var shown = usefulLengths(lengths, countsFor);
    var needsJoining = false;

    var rows = widths.map(function (width) {
      var coverage = num(width.coverage, width.nominal);
      var cells = [width.nominal + '" Boards'];

      shown.forEach(function (len) {
        if (len < runFt) needsJoining = true;
        cells.push(qty(floorBoards(coverage, len, alongLength), len < runFt ? '*' : ''));
      });

      return cells;
    });

    return {
      html:
        statGrid([
          stat('Floor area', fmt(d.floorArea, 0) + ' sq ft'),
          stat('Board direction', 'Along ' + runLabel + ' wall')
        ]) + group('', table(lengthHeaders(shown, 'Board width'), rows)),
      notes: needsJoining ? [label(config, 'joining', '')] : []
    };
  };

  renderers.benches = function (d, config) {
    var withStrapping = 2 * (d.strappingIn + d.panellingIn);
    var withoutStrapping = 2 * d.panellingIn;

    function len(totalInches, deduction) {
      return Math.max(0, Math.floor(totalInches - deduction));
    }

    var rows = [
      ['Along the length wall', inches(len(d.lengthIn, withStrapping)), inches(len(d.lengthIn, withoutStrapping))],
      ['Along the width wall', inches(len(d.widthIn, withStrapping)), inches(len(d.widthIn, withoutStrapping))]
    ];

    return {
      html: group('', table(['Placement', 'Strapping + panelling', 'Panelling only'], rows))
    };
  };

  /* ------------------------------------------------------ custom elements */

  if (!window.customElements.get('sauna-dimensions')) {
    window.customElements.define(
      'sauna-dimensions',
      class extends HTMLElement {
        connectedCallback() {
          try {
            setBuildup(JSON.parse(this.dataset.saunaBuildup || '{}'));
          } catch (error) {
            setBuildup({});
          }

          try {
            this.labels = JSON.parse(this.dataset.saunaLabels || '{}');
          } catch (error) {
            this.labels = {};
          }

          this.inputs = Array.prototype.slice.call(this.querySelectorAll('[data-sauna-field]'));
          this.summary = this.querySelector('[data-sauna-summary]');
          this.resetButton = this.querySelector('[data-sauna-reset]');

          this.onInput = this.onInput.bind(this);
          this.onReset = this.onReset.bind(this);

          this.inputs.forEach(
            function (input) {
              input.addEventListener('input', this.onInput);
              input.addEventListener('change', this.onInput);
            }.bind(this)
          );

          if (this.resetButton) this.resetButton.addEventListener('click', this.onReset);

          this.hydrate();
        }

        hydrate() {
          if (raw) {
            this.inputs.forEach(function (input) {
              var value = raw[input.dataset.saunaField];
              input.value = value === 0 || value ? value : '';
            });
          } else {
            var hasDefault = this.inputs.some(function (input) {
              return input.value !== '';
            });
            if (hasDefault) this.commit();
          }

          this.renderSummary();
          // The build-up may differ from the default, so let every calculator
          // re-render against it.
          broadcast();
        }

        onInput() {
          this.commit();
        }

        onReset() {
          this.inputs.forEach(function (input) {
            input.value = '';
          });
          raw = null;
          dims = null;
          save();
          this.renderSummary();
          broadcast();
          this.inputs[0].focus();
        }

        commit() {
          var next = {};
          this.inputs.forEach(function (input) {
            var value = parseFloat(input.value);
            next[input.dataset.saunaField] = isNaN(value) || value < 0 ? 0 : value;
          });

          raw = next;
          dims = computeDims(next);
          save();
          this.renderSummary();
          broadcast();
        }

        renderSummary() {
          if (!this.summary) return;

          if (!dims) {
            this.summary.innerHTML = '';
            this.summary.hidden = true;
            return;
          }

          this.summary.hidden = false;
          this.summary.innerHTML = [
            stat('Framed volume', fmt(dims.volume, 0) + ' cu ft'),
            stat(
              'Finished inside',
              inchLabel(dims.finishedLengthIn) +
                ' × ' +
                inchLabel(dims.finishedWidthIn) +
                ' × ' +
                inchLabel(dims.finishedHeightIn),
              'sauna-stat__value--sm'
            ),
            stat('Wall area', fmt(dims.wallArea, 0) + ' sq ft'),
            stat('Ceiling area', fmt(dims.ceilingArea, 0) + ' sq ft')
          ].join('');
        }
      }
    );
  }

  if (!window.customElements.get('sauna-calc')) {
    window.customElements.define(
      'sauna-calc',
      class extends HTMLElement {
        connectedCallback() {
          this.results = this.querySelector('[data-sauna-results]');
          this.notes = this.querySelector('[data-sauna-notes]');
          this.type = this.dataset.saunaType;

          try {
            this.config = JSON.parse(this.dataset.saunaConfig || '{}');
          } catch (error) {
            this.config = {};
          }

          this.onUpdate = this.render.bind(this);
          document.addEventListener(EVENT_NAME, this.onUpdate);
          this.render();
        }

        disconnectedCallback() {
          document.removeEventListener(EVENT_NAME, this.onUpdate);
        }

        setNotes(notes) {
          if (!this.notes) return;

          var markup = (notes || [])
            .filter(function (note) {
              return note && String(note).trim() !== '';
            })
            .map(function (note) {
              return '<p>' + esc(note) + '</p>';
            })
            .join('');

          this.notes.innerHTML = markup;
          this.notes.hidden = markup === '';
        }

        render() {
          if (!this.results) return;

          var renderer = renderers[this.type];

          if (!dims || !renderer) {
            this.results.innerHTML =
              '<p class="sauna-empty">Enter your sauna room dimensions at the ' +
              '<a href="#sauna-dimensions">top of the page</a> and we\u2019ll work out how much you need.</p>';
            this.setNotes([]);
            return;
          }

          var output = renderer(dims, this.config);
          this.results.innerHTML = output.html;
          this.setNotes(output.notes);
        }
      }
    );
  }

  /* ------------------------------------------------------------ public API */

  window.SaunaCalc = {
    eventName: EVENT_NAME,
    get: function () {
      return dims;
    },
    getRaw: function () {
      return raw;
    },
    set: function (next) {
      raw = next;
      dims = computeDims(next);
      save();
      broadcast();
    },
    setBuildup: setBuildup,
    parseMeasure: parseMeasure,
    inchLabel: inchLabel,
    boardsForRuns: boardsForRuns,
    usefulLengths: usefulLengths,
    renderers: renderers
  };
})();
